/**
 * Versioned Ramp package fixtures for explicit debug URLs.
 *
 * AB#333: pins the CanonicalRamp contract required by IE-TGD-K-1.2.5 —
 * `effectiveWidthMm` and `handrailSideCount` — with explicit provenance /
 * unknown handling. The `missing-input` variant below deliberately omits
 * `effectiveWidthMm` because that is exactly what the real RevitRampAdapter
 * emits today (Revit exposes no ramp width parameter); it must stream a
 * cited Inconclusive finding, never a fabricated pass or fail.
 */

import type { CanonicalFixture } from './types';
import { buildCanonicalPackage } from './package';

const RAMP_SCHEMA_VERSION = '1.0.0';
const COMPAT: readonly string[] = Object.freeze([RAMP_SCHEMA_VERSION]);

const wrapperContext = Object.freeze({
    BuildingType: 'SingleFamily',
    PlaceName: 'Dublin, Ireland',
    Latitude: 53.3498,
    Longitude: -6.2603,
    NumberOfLevels: 3,
});

const mm = (value: number) => ({ value, unit: 'mm' });

export const rampFixtures: readonly CanonicalFixture[] = Object.freeze([
    {
        elementType: 'ramp',
        variant: 'compliant',
        schemaVersion: RAMP_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Ramp',
        payload: buildCanonicalPackage('ramp', {
            id: 'ramp-1',
            type: 'CanonicalRamp',
            properties: {
                effectiveWidthMm: mm(900),
                handrailSideCount: 1,
            },
        }),
        metadata: {
            description:
                'Ramp narrower than 1000 mm with a handrail on one side, satisfying TGD Part K clause 1.2.5.',
            expectedRuleIds: ['IE-TGD-K-1.2.5'],
            expectedVerdict: 'pass',
        },
    },
    {
        elementType: 'ramp',
        variant: 'noncompliant',
        schemaVersion: RAMP_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Ramp',
        payload: buildCanonicalPackage('ramp', {
            id: 'ramp-noncompliant-1',
            type: 'CanonicalRamp',
            properties: {
                effectiveWidthMm: mm(1200),
                handrailSideCount: 1,
            },
        }),
        metadata: {
            description:
                'Ramp 1200 mm wide (>= 1000 mm) with only one handrail; TGD Part K clause 1.2.5 requires handrails on both sides.',
            expectedRuleIds: ['IE-TGD-K-1.2.5'],
            expectedVerdict: 'fail',
        },
    },
    {
        elementType: 'ramp',
        variant: 'missing-input',
        schemaVersion: RAMP_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Ramp',
        payload: buildCanonicalPackage('ramp', {
            id: 'ramp-missing-1',
            type: 'CanonicalRamp',
            properties: {
                // effectiveWidthMm deliberately omitted: this is the real,
                // honest shape emitted by the Revit adapter today because
                // Revit exposes no ramp width parameter. Must never be
                // inferred - the live API streams a cited Inconclusive
                // finding for rules that require it.
                handrailSideCount: 2,
            },
        }),
        metadata: {
            description:
                'Ramp missing effectiveWidthMm (unknown, as genuinely emitted by the Revit adapter); the live API streams a cited inconclusive finding rather than inferring compliance.',
            expectedVerdict: 'inconclusive',
            missingInput: true,
        },
    },
    {
        elementType: 'ramp',
        variant: 'invalid',
        schemaVersion: RAMP_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Ramp',
        payload: buildCanonicalPackage('ramp', {
            id: 'ramp-invalid-1',
            type: 'CanonicalRamp',
            properties: {
                effectiveWidthMm: 'wide' as unknown as ReturnType<typeof mm>,
                handrailSideCount: -1,
            },
        }),
        metadata: {
            description:
                'Ramp payload with wrong types and a negative handrail count; the live API accepts it and streams a cited inconclusive type-error finding.',
            expectedVerdict: 'inconclusive',
            expectedEvidence: 'type-error',
            invalid: true,
        },
    },
]);
